import { BREAKPOINTS } from "../config/breakpoints";
import React, { useEffect, useRef, useState } from "react";
import usePolling from "../util/hooks/usePolling";
import { isOpenTask } from "./TaskList/taskList.helpers";
import styled from "styled-components";
import { Trow, Tcol, Table, Tbody, Thead } from "../Generic/Table";
import { TASK_STATUSES, OPERATION_STATUSES, OPERATIONS, OPERATION_STATUS_REVERSE_MAP, DIRECTIONS } from "./TaskConstants";
import { errorNotification } from "../Generic/Notifications";
import EmptyState from "../Generic/EmptyState";
import { isEmpty } from "../util/util";
import { useNavigate, useParams } from "react-router-dom";
import TaskService from "./services";
import Loader from "../Generic/Loader";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as TaskActions from "./actions/taskActions";
import { CSSTransition } from "react-transition-group";
import close from "../Assets/Close.png";
import Button from "../Generic/Button";
import ModalButton from "../Generic/ModalButton";
import ImageDownloader from "../Generic/ImageDownloader";
import Timestamp from "../Generic/Timestamp";
import { zIndex } from "../config/zIndex";

const Page = styled.div`
	display: flex;
	height: max-content;
	overflow-y: auto;
	overflow-x: hidden;
	flex-direction: column;
	text-align: left;
	width: 100%;
	padding: 2rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
`;

const Header = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 1rem;
	margin: 0 0 1rem 0;
`;

const Head = styled.div`
	margin: ${(props) => props.margin};
	font-size: 20px;
	font-weight: 600;
`;

const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const Title = styled.div`
	text-align: left;
	font-weight: 600;
	font-size: 2rem;
`;

const FetchingHint = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	font-size: 0.85rem;
	font-weight: 400;
	color: rgba(0, 0, 0, 0.5);
`;

const FetchingSpinner = styled.span`
	width: 0.75rem;
	height: 0.75rem;
	border-radius: 50%;
	border: 2px solid rgba(0, 0, 0, 0.15);
	border-top-color: rgba(0, 0, 0, 0.5);
	animation: task-fetch-spin 0.6s linear infinite;
	flex-shrink: 0;
	@keyframes task-fetch-spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

const GroupContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	margin: ${(props) => props.margin || "0"};
	padding: 1.5rem;
	border-radius: 10px;
	background: #fff;
`;

const Info = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: left;
	flex-wrap: wrap;
	height: max-content;
	width: 100%;
	gap: 2rem 0;
`;

const InfoContent = styled.div`
	flex: 1;
	flex-basis: ${(props) => (props.fullWidth ? "100%" : props.columns ? `${props.columns * 25}%` : "25%")};
	flex-grow: 0;
	flex-direction: column;
	gap: 0.5rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		flex-basis: ${(props) => (props.fullWidth ? "100%" : "45%")};
	}
`;

const InfoHead = styled.div`
	color: rgba(0, 0, 0, 0.5);
	width: max-content;
`;

const InfoData = styled.div`
	margin: 0.3rem 0 0 0;
	font-weight: ${props => props.bold ? "600" : "400"};
`;

const Center = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-family: ${(props) => props.fontFamily};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
	gap: ${(props) => props.gap};
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const ConfirmModal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	height: max-content;
	width: 60%;
	border-radius: 10px;
	overflow-x: hidden;
	z-index: ${zIndex.modal};
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		width: 95%;
	}
`;

const ScrollableDiv = styled.div`
	overflow-y: scroll;
	max-height: ${(props) => props.maxHeight || "60vh"};
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-family: ${(props) => props.fontFamily};
`;

function millisToMinutesAndSeconds(millis) {
	if (!millis) return "-";
	var minutes = Math.floor(millis / 60000);
	var seconds = ((millis % 60000) / 1000).toFixed(0);
	return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
}

function beautifedDuration(duration) {
	if (!duration) return "-";
	var minutes = Math.floor(duration / 60000),
		seconds = Math.floor((duration % 60000) / 1000),
		milliseconds = Math.floor(duration % 1000),
		outputString = "";

	if (minutes) {
		outputString += (minutes + "").padStart(2, "0") + "m ";
	}

	if (seconds) {
		outputString += (seconds + "").padStart(2, "0") + "s ";
	}

	if (milliseconds) {
		outputString += milliseconds + "ms";
	}

	return outputString || "-";
}

const ErroredBackground = "rgba(244, 66, 54, 0.2)";

const POLL_INTERVAL_MS = 10 * 1000;

function hasNamedOperationPayload(namedOperationPayload) {
	return Array.isArray(namedOperationPayload) && namedOperationPayload.length > 0;
}

function needsLiveData(taskData, kitchenId, taskId) {
	return Boolean(kitchenId && taskId && isOpenTask(taskData));
}

const Task = (props) => {
	const { machineID, kitchenId } = props;
	const { taskId } = useParams();
	const [taskData, setTaskData] = useState({});
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [operationData, setOperationData] = useState();
	const [operationModal, toggleOperationModal] = useState(false);
	const [legacyOperationData, setLegacyOperationData] = useState();
	const [legacyOperationModal, toggleLegacyOperationModal] = useState(false);
	const [taskDataModal, toggleTaskDataModal] = useState(false);
	const [taskResponseModal, toggleTaskResponseModal] = useState(false);
	const [thermalImage, setThermalImage] = useState(null);
	const navigate = useNavigate();
	const watchedLiveTaskRef = useRef(false);

	async function getTaskData({ showLoader = true } = {}) {
		if (showLoader) {
			setLoading(true);
		} else {
			setRefreshing(true);
		}
		const response = await TaskService.getTaskData(kitchenId, taskId);
		const { data, error } = response;
		if (data) {
			const task = data[0];
			setTaskData(task);
			if (isOpenTask(task)) {
				watchedLiveTaskRef.current = true;
			}
			if (!isOpenTask(task)) {
				getTaskAssets(task);
			}
		}
		if (error) {
			if (showLoader) {
				errorNotification(error);
			} else {
				errorNotification(error, { toastId: "task-detail-poll-error" });
			}
		}
		if (showLoader) {
			setLoading(false);
		} else {
			setRefreshing(false);
		}
	}

	async function getTaskAssets(taskData) {
		const payload = {
			orderId: taskData.taskGroupId,
			taskId,
		};
		const response = await TaskService.getTaskAssets(payload);
		const { data, error } = response;
		if (data) {
			setThermalImage(data);
		}
		if (error) {
			//NOTE: Right now we generically check if there is a thermal image, so showing an error is not the right way here as there might not be image for certain tasks.
			//TODO: Get this info from server.
		}
	}

	function openOperationModal(payload) {
		setOperationData(payload);
		toggleOperationModal(true);
	}

	function closeOperationModal(payload) {
		toggleOperationModal(false);
		setOperationData(null);
	}

	function openLegacyOperationModal(payload) {
		setLegacyOperationData(payload);
		toggleLegacyOperationModal(true);
	}

	function closeLegacyOperationModal() {
		toggleLegacyOperationModal(false);
		setLegacyOperationData(null);
	}

	function renderTaskRecord({ taskRecord, thermalImage }) {
		let renderData = [],
			tasksRenderData = [],
			operationsRenderData = [];
		if (taskRecord && !isEmpty(taskRecord)) {
			const {
				taskGroupType,
				taskGroupId,
				status,
				taskType,
				actualStartTime,
				actualEndTime,
				estimatedStartTime,
				estimatedEndTime,
				taskReport,
			} = taskRecord;
			const headerStartTime = actualStartTime || estimatedStartTime;
			const headerEndTime = actualEndTime || estimatedEndTime;
			if (taskReport) {
				const { operationsReport } = taskReport;
				if (operationsReport && Object.keys(operationsReport).length) {
					for (const operationId in operationsReport) {
						const operationData = operationsReport[operationId];
						const {
							operationCode,
							startTime,
							endTime,
							timeForOperation,
							status: operationStatus,
							responseBody,
							namedOperationPayload,
							operationTitle,
							operationDetails,
						} = operationData;

						const elementRawProps = {
							key: operationId,
						};

						if (operationStatus === OPERATION_STATUS_REVERSE_MAP.FAILED) {
							elementRawProps["background"] = ErroredBackground;
						}

						//Response code can be zero.
						let operationMetaData;

						if (typeof responseBody?.operationErrorCode !== "undefined") {
							operationMetaData = responseBody.operationErrorCode;
						} else if (typeof responseBody?.responseCode !== "undefined") {
							operationMetaData = responseBody.responseCode;
						} else {
							operationMetaData = "-";
						}

						const operationLabel = operationTitle || OPERATIONS[operationCode];

						operationsRenderData.push(
							<Trow {...elementRawProps} hoverEffect={false} breakpoint="800px">
								<Tcol padding="1rem 0" borderRadius="10px 0 0 10px" title="Operation Id" breakpoint="800px">
									{operationId}
								</Tcol>
								<Tcol padding="1rem 0" title="Operation" breakpoint="800px">{operationLabel}</Tcol>
								<Tcol padding="1rem 0" title="Duration" breakpoint="800px">{beautifedDuration(timeForOperation)}</Tcol>
								<Tcol padding="1rem 0" title="Start Time" breakpoint="800px"><Timestamp value={startTime} /></Tcol>
								<Tcol padding="1rem 0" title="End Time" breakpoint="800px"><Timestamp value={endTime} /></Tcol>
								<Tcol padding="1rem 0" title="Status" breakpoint="800px">{OPERATION_STATUSES?.[operationStatus] || "-"}</Tcol>
								<Tcol padding="1rem 0" title="Data" breakpoint="800px">{operationMetaData}</Tcol>
								<Tcol padding="0.8rem 0" borderRadius="0px 10px 10px 0px" title="Info" breakpoint="800px">
									{hasNamedOperationPayload(namedOperationPayload) ? (
										<span onClick={() => openOperationModal(operationData)} class="material-symbols-outlined info-symbol">
											info
										</span>
									) : operationDetails ? (
										<span onClick={() => openLegacyOperationModal(operationData)} class="material-symbols-outlined info-symbol">
											info
										</span>
									) : (
										""
									)}
								</Tcol>
							</Trow>
						);
					}
				}
			}
			renderData.push(
				<>
					<GroupContainer>
						<Info>
							<InfoContent>
								<InfoHead>TaskType</InfoHead>
								<InfoData>{taskType}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>Task Group Type</InfoHead>
								<InfoData>{taskGroupType}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>Task Group Id</InfoHead>
								<InfoData>{taskGroupId}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>Status</InfoHead>
								<InfoData>{TASK_STATUSES[status]}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>Start Time</InfoHead>
								<InfoData><Timestamp value={headerStartTime} /></InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>End Time</InfoHead>
								<InfoData><Timestamp value={headerEndTime} /></InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>Duration</InfoHead>
								<InfoData>{millisToMinutesAndSeconds(taskReport?.taskTime)}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoData>
									<Div width="100%" gap="1rem">
										<Button onClick={() => toggleTaskDataModal(true)}>Task Data</Button>
										<Button onClick={() => toggleTaskResponseModal(true)}>Task Response</Button>
										{thermalImage ? (
											<ModalButton
												title="Thermal Image"
												buttonText="Get Thermal Image"
												render={() => <ImageDownloader imageUrl={thermalImage} />}
											/>
										) : (
											""
										)}
									</Div>
								</InfoData>
							</InfoContent>
						</Info>
					</GroupContainer>
					<Head margin="1rem 0 0 0">Operations</Head>
					{operationsRenderData.length ? (
						<Table breakpoint="800px">
							<Trow head breakpoint="800px">
								<Thead>Id</Thead>
								<Thead>Operation</Thead>
								<Thead>Duration</Thead>
								<Thead>Start Time</Thead>
								<Thead>End Time</Thead>
								<Thead>Status</Thead>
								<Thead>Data</Thead>
								<Thead></Thead>
							</Trow>
							<Tbody>{operationsRenderData}</Tbody>
						</Table>
					) : (
						<Center>No Report found for this task</Center>
					)}
					<Tbody>{tasksRenderData}</Tbody>
				</>
			);
		} else {
			<EmptyState retry={getTaskData} />;
		}
		return renderData;
	}

	function renderTaskResponseModal(payload) {
		return (
			<>
				<Div color="#fff" background="rgba(45, 35, 66, 0.8)" padding="1rem" jContent="space-between">
					<Span fontSize="1.4rem">Task Response</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={() => {
							toggleTaskResponseModal(false);
						}}
					/>
				</Div>
				<ScrollableDiv padding="1.5rem" jContent="space-around" flexDirection="column">
					{isEmpty(payload.taskResponse) ? (
						<Div>No Task Response found</Div>
					) : (
						<pre>{JSON.stringify(payload.taskResponse, null, 4)}</pre>
					)}
				</ScrollableDiv>
			</>
		);
	}

	function renderTaskDataModal(payload) {
		return (
			<>
				<Div color="#fff" background="rgba(45, 35, 66, 0.8)" padding="1rem" jContent="space-between">
					<Span fontSize="1.4rem">Task Data</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={() => {
							toggleTaskDataModal(false);
						}}
					/>
				</Div>
				<ScrollableDiv padding="1.5rem" jContent="space-around" flexDirection="column">
					{isEmpty(payload.taskData) ? <Div>No Task Data found</Div> : <pre>{JSON.stringify(payload.taskData, null, 4)}</pre>}
				</ScrollableDiv>
			</>
		);
	}

	function renderNamedPayloadField({ key, title, value, unit }) {
		if (value === undefined || value === null) {
			return null;
		}
		switch (typeof value) {
			case "string":
			case "number":
			case "boolean":
				return (
					<InfoContent key={key}>
						<InfoHead>{title}</InfoHead>
						<InfoData bold>
							{value}
							{unit ? `${unit}` : ""}
						</InfoData>
					</InfoContent>
				);
			case "object":
				return (
					<InfoContent key={key} fullWidth>
						<InfoHead>{title}</InfoHead>
						<InfoData>
							<pre>{JSON.stringify(value, null, 4)}</pre>
						</InfoData>
					</InfoContent>
				);
			default:
				return null;
		}
	}

	function renderOperationModal(payload) {
		if (!payload) {
			return null;
		}
		const { namedOperationPayload, operationTitle, operationCode } = payload;
		return (
			<>
				<Div color="#fff" background="rgba(45, 35, 66, 0.8)" padding="1rem" jContent="space-between">
					<Span fontSize="1.4rem">{operationTitle || OPERATIONS[operationCode]}</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={() => {
							closeOperationModal();
						}}
					/>
				</Div>
				<ScrollableDiv padding="0" jContent="space-around" flexDirection="column">
					<GroupContainer margin="0">
						<Info>
							{Array.isArray(namedOperationPayload)
								? namedOperationPayload.map((field) => renderNamedPayloadField(field))
								: null}
						</Info>
					</GroupContainer>
				</ScrollableDiv>
			</>
		);
	}

	// TODO: Remove this legacy dispense modal (state, open/close, renderer, CSSTransition, and DIRECTIONS) after some time.
	// Earlier tasks have no namedOperationPayload and still store operationDetails.
	function renderLegacyOperationModal(payload) {
		if (!payload) {
			return null;
		}
		const { operationDetails, operationPayload, operationTitle, operationCode } = payload;
		const { moduleId, systemId, dispenserType, direction, degreeToRotate, dispensingRPM, expectedDispenseAmount, itemId } =
			operationDetails;
		const module = moduleId || operationPayload?.[0] || "-";
		const system = systemId || operationPayload?.[1] || "-";

		return (
			<>
				<Div color="#fff" background="rgba(45, 35, 66, 0.8)" padding="1rem" jContent="space-between">
					<Span fontSize="1.4rem">{operationTitle || OPERATIONS[operationCode]}</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={() => {
							closeLegacyOperationModal();
						}}
					/>
				</Div>
				<ScrollableDiv padding="0" jContent="space-around" flexDirection="column">
					<GroupContainer margin="0">
						<Info>
							<InfoContent>
								<InfoHead>Module</InfoHead>
								<InfoData>{module}</InfoData>
							</InfoContent>
							<InfoContent>
								<InfoHead>System</InfoHead>
								<InfoData>{system}</InfoData>
							</InfoContent>
							{direction !== null && direction !== undefined ? (
								<InfoContent>
									<InfoHead>Direction</InfoHead>
									<InfoData>{DIRECTIONS[direction]}</InfoData>
								</InfoContent>
							) : (
								""
							)}
							{dispenserType ? (
								<InfoContent>
									<InfoHead>Dispenser Type</InfoHead>
									<InfoData>{dispenserType}</InfoData>
								</InfoContent>
							) : (
								""
							)}
							{degreeToRotate ? (
								<InfoContent>
									<InfoHead>Degrees to Rotate</InfoHead>
									<InfoData>{degreeToRotate}</InfoData>
								</InfoContent>
							) : (
								""
							)}
							{dispensingRPM ? (
								<InfoContent>
									<InfoHead>Dispensing Rpm</InfoHead>
									<InfoData>{dispensingRPM}</InfoData>
								</InfoContent>
							) : (
								""
							)}
							{expectedDispenseAmount ? (
								<InfoContent>
									<InfoHead>Expected Dispense Amount</InfoHead>
									<InfoData>{expectedDispenseAmount}</InfoData>
								</InfoContent>
							) : (
								""
							)}
							{itemId ? (
								<InfoContent>
									<InfoHead>Item</InfoHead>
									<InfoData>{itemId}</InfoData>
								</InfoContent>
							) : (
								""
							)}
						</Info>
					</GroupContainer>
				</ScrollableDiv>
			</>
		);
	}

	function handleEscapeKeyDown(e) {
		if (e.key === "Escape") {
			closeOperationModal();
			toggleOperationModal(false);
			closeLegacyOperationModal();
		}
	}

	function addEventListeners() {
		document.addEventListener("keydown", handleEscapeKeyDown);
	}

	function removeEventListeners() {
		document.removeEventListener("keydown", handleEscapeKeyDown);
	}

	useEffect(() => {
		getTaskData();
		addEventListeners();
		return () => {
			removeEventListeners();
		};
	}, []);

	usePolling(() => getTaskData({ showLoader: false }), {
		interval: POLL_INTERVAL_MS,
		enabled: needsLiveData(taskData, kitchenId, taskId),
	});

	return (
		<Page>
			<Header>
				<span
					onClick={() =>
						navigate(`/${machineID}/task/recent`, {
							state: { preserveState: !watchedLiveTaskRef.current },
						})
					}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<TitleRow>
					<Title>{taskId}</Title>
					{refreshing ? (
						<FetchingHint>
							<FetchingSpinner aria-hidden />
							Updating...
						</FetchingHint>
					) : null}
				</TitleRow>
			</Header>
			{!loading ? renderTaskRecord({ taskRecord: taskData, thermalImage }) : ""}
			{loading ? (
				<Center>
					<Loader />
				</Center>
			) : (
				""
			)}
			<CSSTransition in={operationModal} timeout={1000} classNames="module" unmountOnExit>
				<Overlay>
					<ConfirmModal>{renderOperationModal(operationData)}</ConfirmModal>
				</Overlay>
			</CSSTransition>
			{/* TODO: Remove with the legacy operation modal state after some time. */}
			<CSSTransition in={legacyOperationModal} timeout={1000} classNames="module" unmountOnExit>
				<Overlay>
					<ConfirmModal>{renderLegacyOperationModal(legacyOperationData)}</ConfirmModal>
				</Overlay>
			</CSSTransition>

			<CSSTransition in={taskDataModal} timeout={1000} classNames="module" unmountOnExit>
				<Overlay>
					<ConfirmModal>{renderTaskDataModal(taskData)}</ConfirmModal>
				</Overlay>
			</CSSTransition>

			<CSSTransition in={taskResponseModal} timeout={1000} classNames="module" unmountOnExit>
				<Overlay>
					<ConfirmModal>{renderTaskResponseModal(taskData)}</ConfirmModal>
				</Overlay>
			</CSSTransition>
		</Page>
	);
};

const mapStateToProps = (state) => {
	const { task, landing } = state;
	return { ...task, ...landing };
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			...TaskActions,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(Task);
